import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "helpers/cn";

const titleVariants = cva("m-0 h-auto font-heading text-black/88", {
  variants: {
    variant: {
      display: "text-display",
      xl: "text-heading-xl",
      lg: "text-heading-lg",
      md: "text-heading-md",
      sm: "text-heading-sm",
      xs: "text-heading-xs"
    },
    weight: {
      semibold: "font-semibold",
      bold: "font-bold",
      heavy: "font-heavy"
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right"
    },
    transform: {
      uppercase: "uppercase",
      capitalize: "capitalize"
    },
    spacing: {
      sm: "mb-2",
      md: "mb-2.5",
      lg: "mb-3"
    },
    truncate: { true: "truncate" }
  }
});

type TitleVariantProps = VariantProps<typeof titleVariants>;

type Level = 1 | 2 | 3 | 4 | 5 | 6;

type TitleProps = Omit<ComponentProps<"h1">, keyof TitleVariantProps> &
  TitleVariantProps & { level: Level };

const LEVEL_VARIANTS = {
  1: "xl",
  2: "lg",
  3: "md",
  4: "sm",
  5: "xs",
  6: "xs"
} as const satisfies Record<Level, TitleVariantProps["variant"]>;

const Title = ({
  className,
  level,
  variant,
  weight,
  align,
  transform,
  spacing,
  truncate,
  ...props
}: TitleProps) => {
  const Heading = `h${level}` as const;

  return (
    <Heading
      className={cn(
        titleVariants({
          variant: variant ?? LEVEL_VARIANTS[level],
          weight,
          align,
          transform,
          spacing,
          truncate
        }),
        className
      )}
      data-slot="title"
      {...props}
    />
  );
};

export { Title, titleVariants };
export type { TitleProps };
